import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

import { DomainService, IDomain, TokenService } from 'viggo-sistemas-core-web';

import { ICentroResultado } from '@app/modules/centro_resultado/model/centro_resultado.model';
import { CentroResultadoService } from '@app/modules/centro_resultado/services/centro_resultado.service';
import { INaturezaFinanceira } from '@app/modules/natureza_financeira/model/natureza_financeira.model';
import { NaturezaFinanceiraService } from '@app/modules/natureza_financeira/services/natureza_financeira.service';
import { PedidoService } from '@app/modules/pedidos/services/pedido.service';
import { ValidadorSettingsDomain } from '@app/shared/utils/validar_settings_domain';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { IRole } from 'viggo-sistemas-core-web/modules/role/model/role.model';
import { DomainSettingsService } from './domain-settings.service';

@Injectable({ providedIn: 'root' })
export class DomainSettingsResolveService  {
  constructor(
    protected domainService: DomainService,
    protected domainSettingsService: DomainSettingsService,
    protected tokenService: TokenService,
    protected pedidoService: PedidoService,
    protected naturezaFinanceiraService: NaturezaFinanceiraService,
    protected centroResultadoService: CentroResultadoService,
    protected router: Router
  ) {}

  public getSettingsObj = new Subject<any>();

  getObjNatFin(domain: IDomain): Observable<any> {
    const observers = domain.settings['regras'].config_financeira.operacoes.map(
      op => {
        return this.naturezaFinanceiraService
          .getEntity(op.natureza_financeira_id)
          .pipe(
            map((value: INaturezaFinanceira): any => ({
              ...op,
              natureza_financeira: value
            }))
          );
      }
    );
    return forkJoin(observers);
  }

  getObjCentro(domain: IDomain): Observable<any> {
    const observers = domain.settings['regras'].config_financeira.operacoes.map(
      op => {
        return this.centroResultadoService
          .getEntity(op.centro_resultado_id)
          .pipe(
            map((value: ICentroResultado): any => ({
              ...op,
              centro_resultado: value
            }))
          );
      }
    );

    return forkJoin(observers);
  }

  public resolve(route: ActivatedRouteSnapshot): Observable<IDomain> {
    const id = route.paramMap.get('id');
    localStorage.setItem('pedidosEmAberto', 'false');

    const getOperacoesFinanceira = () => {
      return mergeMap((entity: IDomain) => {
        entity.settings = new ValidadorSettingsDomain(
          entity.settings
        ).getDomainSettingsBody(entity.settings) as any;

        if (Object.keys(entity.settings).length === 0) {
          return of({ ...entity });
        } else if (Object.keys(entity.settings['regras']).length === 0) {
          return of({ ...entity });
        } else if (
          entity.settings['regras'].config_financeira.operacoes === undefined
        ) {
          return of({ ...entity });
        } else if (
          entity.settings['regras'].config_financeira.operacoes.length === 0
        ) {
          return of({ ...entity });
        }
        var newSettings = {
          ...entity.settings,
          regras: {
            controle: {
              ...entity.settings['regras'].controle
            },
            config_financeira: {
              ...entity.settings['regras'].config_financeira,
              observables_fin: this.getObjNatFin(entity)
            },
            caixa_pdv: {
              ...entity.settings['regras'].caixa_pdv
            }
          }
        };
        return of({
          ...entity,
          settings: newSettings
        } as IDomain);
      });
    };

    const getOperacoesCentros = () => {
      return mergeMap((entity: IDomain) => {
        entity.settings = new ValidadorSettingsDomain(
          entity.settings
        ).getDomainSettingsBody(entity.settings) as any;

        if (Object.keys(entity.settings).length === 0) {
          return of({ ...entity });
        } else if (Object.keys(entity.settings['regras']).length === 0) {
          return of({ ...entity });
        } else if (
          entity.settings['regras'].config_financeira.operacoes === undefined
        ) {
          return of({ ...entity });
        } else if (
          entity.settings['regras'].config_financeira.operacoes.length === 0
        ) {
          return of({ ...entity });
        }

        var newSettings = {
          ...entity.settings,
          regras: {
            controle: {
              ...entity.settings['regras'].controle
            },
            config_financeira: {
              // ...entity.settings['regras'].config_financeiraa.operacao_padrao_pagar,
              // ...entity.settings['regras'].config_financeir.operacao_padrao_receber,
              ...entity.settings['regras'].config_financeira,
              observables_centro: this.getObjCentro(entity)
            },
            caixa_pdv: {
              ...entity.settings['regras'].caixa_pdv
            }
          }
        };
        return of({
          ...entity,
          settings: newSettings
        } as IDomain);
      });
    };

    const checkPedidosEmAberto = () => {
      return mergeMap((entity: IDomain) => {
        if (id) return of(entity);
        const req = this.pedidoService.getEntitiesByParams(
          `domain_id=${
            this.tokenService.getDomain().id
          }&page=0&page_size=1&status=CRIADO,EM_ANDAMENTO,PENDENTE,CONCLUIDO_PARTES`,
          undefined,
          true
        );
        return req.pipe(
          map(value => {
            if (value.data.length !== 0)
              localStorage.setItem('pedidosEmAberto', 'true');
            return entity;
          })
        );
      });
    };

    const getRoles = () => {
      return mergeMap((entity: IDomain) => {
        if (!id) return of(entity);
        const req = this.domainSettingsService.getRoles(entity.id);
        return req.pipe(
          map((value: IRole[]) => {
            return ({
              ...entity,
              roles: value
            } as unknown) as IDomain;
          })
        );
      });
    };

    return this.domainService
      .getEntity(id || this.tokenService.getDomain().id, 'application')
      .pipe(
        getOperacoesFinanceira(),
        getOperacoesCentros(),
        checkPedidosEmAberto(),
        getRoles(),
        catchError(() => {
          return of(this.createNewDomainInfo());
        })
      );
  }

  public createNewDomainInfo(): IDomain {
    return {} as IDomain;
  }
}
